import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router';
import { disableUser, getPendingValidationUsers, validateUser } from '../../constants/api';
import { IApiErrorResponse, IApiUser } from '../../constants/api-interfaces';
import { canAccessDashboardRoute } from '../../constants/utils';
import StyledTable from '../../components/reusable/StyledTable';
import { useAuth } from '../../contexts/AuthContext';
import { useSnackbar } from '../../contexts/SnackbarContext';

export default function VerifyUsers() {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const { snackbarDispatch } = useSnackbar();
  const [users, setUsers] = useState<IApiUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<IApiUser | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const pendingUsers = useMemo(
    () => users.filter((user) => !user.isValidated && !user.isDisabled && user.emailVerified),
    [users],
  );

  const currentRole = auth?.user?.role ?? 'teammember';
  const canAccessPage = canAccessDashboardRoute(currentRole, 'verify-users');

  useEffect(() => {
    if (!canAccessPage) {
      navigate('/dashboard', { replace: true });
    }
  }, [canAccessPage, navigate]);

  const fetchPendingUsers = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await getPendingValidationUsers();
      const filteredUsers = (response.data.users || []).filter(
        (user) => !user.isValidated && !user.isDisabled && user.emailVerified,
      );
      setUsers(filteredUsers);

      if (filteredUsers.length === 0) {
        snackbarDispatch({
          type: 'setSnackbar',
          payload: { message: 'No users are currently awaiting validation.', error: false },
        });
      }
    } catch (error) {
      const axiosError = error as AxiosError<IApiErrorResponse>;
      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: axiosError.response?.data?.message || 'Unable to load users awaiting validation.',
          error: true,
        },
      });
    } finally {
      setIsLoading(false);
    }
  }, [snackbarDispatch]);

  useEffect(() => {
    if (!canAccessPage) {
      return;
    }

    fetchPendingUsers();
  }, [canAccessPage, fetchPendingUsers]);

  if (!canAccessPage) {
    return null;
  }

  const handleValidateConfirm = async () => {
    if (!selectedUser) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await validateUser(selectedUser.id);
      setUsers((currentUsers) => currentUsers.filter((user) => user.id !== selectedUser.id));
      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: `${response.data.user?.firstName || 'User'} has been validated successfully.`,
          error: false,
        },
      });
    } catch (error) {
      const axiosError = error as AxiosError<IApiErrorResponse>;
      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: axiosError.response?.data?.message || 'Unable to validate the user.',
          error: true,
        },
      });
    } finally {
      setIsSubmitting(false);
      setSelectedUser(null);
    }
  };

  const handleDisableUser = async (userId: string) => {
    setIsSubmitting(true);

    try {
      const response = await disableUser(userId);
      setUsers((currentUsers) => currentUsers.filter((user) => user.id !== userId));
      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: `${response.data.user?.firstName || 'User'} has been disabled.`,
          error: false,
        },
      });
    } catch (error) {
      const axiosError = error as AxiosError<IApiErrorResponse>;
      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: axiosError.response?.data?.message || 'Unable to disable the user.',
          error: true,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
      <Typography variant="h4" sx={{ fontSize: '28px', fontWeight: 700, mb: 2 }}>
        Verify Users
      </Typography>

      <StyledTable
        columns={[
          { label: 'Employee #' },
          { label: 'Name' },
          { label: 'Email' },
          { label: 'Role' },
          { label: 'Location' },
          { label: 'Actions', align: 'center' },
        ]}
        hasRows={pendingUsers.length > 0}
        isLoading={isLoading}
        loadingMessage="Loading pending users..."
        emptyMessage="No users pending Verification."
      >
        {pendingUsers.map((user) => {
          const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || 'Unnamed user';

          return (
            <TableRow key={user.id} hover>
              <TableCell>{user.employeeNumber || '—'}</TableCell>
              <TableCell>{fullName}</TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>{user.role}</TableCell>
              <TableCell>{user.location?.name || '—'}</TableCell>
              <TableCell align="center">
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                  <IconButton
                    color="success"
                    aria-label="approve user"
                    disabled={isSubmitting}
                    onClick={() => setSelectedUser(user)}
                  >
                    <CheckCircleOutlineIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    aria-label="disable user"
                    disabled={isSubmitting}
                    onClick={() => void handleDisableUser(user.id)}
                  >
                    <CancelOutlinedIcon />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          );
        })}
      </StyledTable>

      <Dialog open={Boolean(selectedUser)} onClose={() => setSelectedUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Validate user?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedUser
              ? `Are you sure you want to validate ${selectedUser.firstName || 'this user'} ${selectedUser.lastName || ''}?`
              : 'Are you sure you want to validate this user?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedUser(null)} variant="outlined" color="error" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleValidateConfirm()} color="success" variant="contained" disabled={isSubmitting}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
